import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import { useAuth } from "@/contexts/AuthContext";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

/**
 * CreateListing — Transaction Lifecycle v1 Step 1
 * All 22 required fields per /app/docs/Architecture/TransactionLifecycle.md.
 */
export default function CreateListing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useSEO({ title: "Create Listing — FinanceLocal", noIndex: true, canonical: "https://myfinancelocal.com/sell/new" });

  const [f, setF] = useState({
    note_type: "vehicle_note",
    asking_price: "", remaining_balance: "", payoff_amount: "",
    interest_rate: "", payment_amount: "", original_amount_financed: "",
    remaining_term_weeks: "", payment_frequency: "monthly",
    vehicle_year: "", vehicle_make: "", vehicle_model: "", vin: "", state: "",
    payments_made: "", payments_remaining: "", current_payment_status: "current",
    description: "", lien_position: "first", title_status: "clean",
    recourse: false, seller_financing_available: false,
    min_acceptable_offer: "",
    grade: "B", category: "performing",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState({ estimated_yield: 0, annual_cashflow: 0, remaining_term_months: 0 });

  // Live server-computed yield preview
  useEffect(() => {
    const pay = Number(f.payment_amount), term = Number(f.remaining_term_weeks), ask = Number(f.asking_price);
    if (!pay || !term || !ask) { setPreview({ estimated_yield: 0, annual_cashflow: 0, remaining_term_months: 0 }); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/seller/yield-preview", {
          weekly_payment: pay, remaining_term_weeks: term,
          asking_price: ask, payment_frequency: f.payment_frequency,
        });
        setPreview(r.data);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [f.payment_amount, f.remaining_term_weeks, f.asking_price, f.payment_frequency]);

  const freq = f.payment_frequency;
  const termLabel    = freq === "monthly" ? "Remaining term (months) *" : freq === "biweekly" ? "Remaining term (bi-weeks) *" : "Remaining term (weeks) *";
  const paymentLabel = freq === "monthly" ? "Monthly payment ($) *" : freq === "biweekly" ? "Bi-weekly payment ($) *" : "Weekly payment ($) *";

  const submit = async () => {
    // Cast numerics
    const num = (v) => (v === "" || v === null ? null : Number(v));
    const req = ["asking_price","remaining_balance","payoff_amount","interest_rate","payment_amount","original_amount_financed","remaining_term_weeks","vehicle_year","vehicle_make","vehicle_model","state","current_payment_status"];
    for (const k of req) { if (!f[k] && f[k] !== 0) { toast.error(`Missing: ${k.replace(/_/g," ")}`); return; } }
    setBusy(true);
    try {
      const payload = {
        note_type: f.note_type,
        asking_price: num(f.asking_price),
        remaining_balance: num(f.remaining_balance),
        payoff_amount: num(f.payoff_amount),
        interest_rate: num(f.interest_rate),
        payment_amount: num(f.payment_amount),
        original_amount_financed: num(f.original_amount_financed),
        remaining_term_weeks: parseInt(f.remaining_term_weeks) || 0,
        payment_frequency: f.payment_frequency,
        vehicle_year: parseInt(f.vehicle_year) || 0,
        vehicle_make: f.vehicle_make,
        vehicle_model: f.vehicle_model,
        vin: f.vin || null,
        state: f.state,
        payments_made: parseInt(f.payments_made) || 0,
        payments_remaining: parseInt(f.payments_remaining) || 0,
        current_payment_status: f.current_payment_status,
        description: f.description,
        lien_position: f.lien_position,
        title_status: f.title_status,
        recourse: !!f.recourse,
        seller_financing_available: !!f.seller_financing_available,
        min_acceptable_offer: f.min_acceptable_offer === "" ? null : num(f.min_acceptable_offer),
        grade: f.grade, category: f.category,
      };
      const r = await api.post("/listings", payload);
      toast.success("Listing created");
      navigate(`/notes/${r.data.note_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create listing");
    } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <div data-testid="page-create-listing" className="mx-auto max-w-4xl px-4 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Marketplace · Sell</div>
        <h1 className="font-display text-4xl mt-2">Create Listing</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Enter all details required by the Transaction Lifecycle v1 specification. Your minimum
          acceptable offer stays private — buyers only see the asking price.
        </p>
      </div>

      <Section title="Note Basics">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Note type" required>
            <select data-testid="f-note_type" value={f.note_type} onChange={(e) => set("note_type", e.target.value)} className="w-full border border-border rounded-none px-3 py-2 text-sm bg-background">
              <option value="vehicle_note">Vehicle-backed note</option>
              <option value="promissory_note">Promissory note</option>
              <option value="real_estate_note">Real estate note</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Current payment status" required>
            <select data-testid="f-current_payment_status" value={f.current_payment_status} onChange={(e) => set("current_payment_status", e.target.value)} className="w-full border border-border rounded-none px-3 py-2 text-sm bg-background">
              <option value="current">Current</option>
              <option value="30_days_late">30 days late</option>
              <option value="60_days_late">60 days late</option>
              <option value="90_days_late">90+ days late</option>
              <option value="charged_off">Charged off</option>
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Pricing & Terms">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Asking price ($)" required><NumInput tid="f-asking_price" v={f.asking_price} on={(v) => set("asking_price", v)} /></Field>
          <Field label="Remaining balance ($)" required><NumInput tid="f-remaining_balance" v={f.remaining_balance} on={(v) => set("remaining_balance", v)} /></Field>
          <Field label="Payoff amount ($)" required><NumInput tid="f-payoff_amount" v={f.payoff_amount} on={(v) => set("payoff_amount", v)} /></Field>
          <Field label="Interest rate (%)" required><NumInput tid="f-interest_rate" v={f.interest_rate} on={(v) => set("interest_rate", v)} step="0.01" /></Field>
          <Field label={paymentLabel.replace(" *", "")} required><NumInput tid="f-payment_amount" v={f.payment_amount} on={(v) => set("payment_amount", v)} /></Field>
          <Field label="Original amount financed ($)" required><NumInput tid="f-original_amount_financed" v={f.original_amount_financed} on={(v) => set("original_amount_financed", v)} /></Field>
          <Field label={termLabel}><NumInput tid="f-remaining_term_weeks" v={f.remaining_term_weeks} on={(v) => set("remaining_term_weeks", v)} step="1" /></Field>
          <Field label="Payment frequency" required>
            <select data-testid="f-payment_frequency" value={f.payment_frequency} onChange={(e) => set("payment_frequency", e.target.value)} className="w-full border border-border rounded-none px-3 py-2 text-sm bg-background">
              <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="monthly">Monthly</option>
            </select>
          </Field>
          <Field label="Payments made"><NumInput tid="f-payments_made" v={f.payments_made} on={(v) => set("payments_made", v)} step="1" /></Field>
          <Field label="Payments remaining"><NumInput tid="f-payments_remaining" v={f.payments_remaining} on={(v) => set("payments_remaining", v)} step="1" /></Field>
        </div>
      </Section>

      <Section title="Collateral (Vehicle)">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Vehicle year" required><NumInput tid="f-vehicle_year" v={f.vehicle_year} on={(v) => set("vehicle_year", v)} step="1" /></Field>
          <Field label="Vehicle make" required><Input data-testid="f-vehicle_make" className="rounded-none" value={f.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} /></Field>
          <Field label="Vehicle model" required><Input data-testid="f-vehicle_model" className="rounded-none" value={f.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} /></Field>
          <Field label="VIN (optional)"><Input data-testid="f-vin" className="rounded-none font-mono-data" value={f.vin} onChange={(e) => set("vin", e.target.value)} /></Field>
          <Field label="State" required>
            <select data-testid="f-state" value={f.state} onChange={(e) => set("state", e.target.value)} className="w-full border border-border rounded-none px-3 py-2 text-sm bg-background">
              <option value="">Select…</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Title status">
            <select data-testid="f-title_status" value={f.title_status} onChange={(e) => set("title_status", e.target.value)} className="w-full border border-border rounded-none px-3 py-2 text-sm bg-background">
              <option value="clean">Clean</option><option value="rebuilt">Rebuilt</option><option value="salvage">Salvage</option><option value="branded">Branded</option>
            </select>
          </Field>
          <Field label="Lien position">
            <select data-testid="f-lien_position" value={f.lien_position} onChange={(e) => set("lien_position", e.target.value)} className="w-full border border-border rounded-none px-3 py-2 text-sm bg-background">
              <option value="first">First</option><option value="second">Second</option><option value="subordinate">Subordinate</option>
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Terms & Description">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Recourse?"><label className="flex items-center gap-2 h-10"><Checkbox data-testid="f-recourse" checked={f.recourse} onCheckedChange={(v) => set("recourse", v === true)} /> Yes, seller retains recourse</label></Field>
          <Field label="Seller financing available?"><label className="flex items-center gap-2 h-10"><Checkbox data-testid="f-seller_financing_available" checked={f.seller_financing_available} onCheckedChange={(v) => set("seller_financing_available", v === true)} /> Yes</label></Field>
        </div>
        <Field label="Brief description / comments">
          <Textarea data-testid="f-description" rows={3} className="rounded-none" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Payment history highlights, borrower story, condition of collateral…" />
        </Field>
      </Section>

      <Section title="Private (only you can see this)">
        <Field label="Hidden minimum acceptable offer ($)">
          <NumInput tid="f-min_acceptable_offer" v={f.min_acceptable_offer} on={(v) => set("min_acceptable_offer", v)} />
        </Field>
        <p className="text-xs text-muted-foreground">Never displayed publicly. Only visible to you and platform administrators.</p>
      </Section>

      <div className="border border-border p-4 grid grid-cols-3 gap-3 bg-muted/20 mb-4" data-testid="cl-yield-preview">
        <div>
          <div className="font-overline text-[10px] text-muted-foreground">Estimated Yield (FinanceLocal-calculated)</div>
          <div className="font-mono-data text-2xl" data-testid="cl-yield-value">{preview.estimated_yield ? `${preview.estimated_yield}%` : "—"}</div>
        </div>
        <div>
          <div className="font-overline text-[10px] text-muted-foreground">Projected Annual Cash Flow</div>
          <div className="font-mono-data text-2xl" data-testid="cl-cashflow-value">{preview.annual_cashflow ? `$${Number(preview.annual_cashflow).toLocaleString()}` : "—"}</div>
        </div>
        <div>
          <div className="font-overline text-[10px] text-muted-foreground">Remaining Term</div>
          <div className="font-mono-data text-2xl">
            {Number(f.remaining_term_weeks) > 0 ? `${f.remaining_term_weeks} ${freq === "monthly" ? "mo" : freq === "biweekly" ? "biwk" : "wk"}` : "—"}
          </div>
        </div>
        <div className="col-span-3 text-[10px] text-muted-foreground">
          FinanceLocal calculates yield and annual cash flow from your entered payment frequency, payment amount, term, and asking price. You cannot override these — the platform owns the calculation for consistency across the marketplace.
        </div>
      </div>

      <div className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 text-xs mb-4" data-testid="cl-review-notice">
        <b>Pending Review:</b> After you publish, a FinanceLocal admin reviews the listing (typically under 1 business day) before it goes live on the marketplace. You'll be notified when it's approved.
      </div>

      <div className="flex gap-3 pt-4">
        <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider" onClick={() => navigate(-1)} data-testid="btn-listing-cancel">Cancel</Button>
        <Button data-testid="btn-listing-submit" onClick={submit} disabled={busy}
          className="rounded-none uppercase text-xs tracking-wider h-11 flex-1">
          {busy ? "Publishing…" : "Publish Listing"}
        </Button>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mb-8">
    <div className="font-overline text-muted-foreground mb-3">{title}</div>
    <div className="space-y-3">{children}</div>
  </section>
);
const Field = ({ label, required, children }) => (
  <div>
    <Label className="font-overline text-xs mb-1 block">{label}{required && " *"}</Label>
    {children}
  </div>
);
const NumInput = ({ tid, v, on, step = "0.01" }) => (
  <Input data-testid={tid} type="number" step={step} className="rounded-none font-mono-data" value={v} onChange={(e) => on(e.target.value)} />
);
